import { useEffect, useRef, useState } from "react";
import { api, puede, incluye, leerSesion, type Paciente, type Odontograma, type Historia, type Nota, type HallazgoApi } from "../api.ts";
import { dinero, edad, nombrePieza, NOMBRE_TIPO, CAMPOS_ANAMNESIS, CAMPOS_EXAMEN, NOMBRE_CAMPO, TIPOS_HALLAZGO, TIPOS_CON_GRADO, rangoPuente, notaTramo, esTramo, SUPERFICIES, CIE10, CIE_POR_HALLAZGO, marcasDeCampo, indicesPorDenticion, MAX_GRADO, type Hallazgo } from "../../../dominio/src/index.ts";
import { OdontogramaSVG, describirHallazgo } from "../componentes/OdontogramaSVG.tsx";
import { Dictador } from "../componentes/Dictador.tsx";
import { AvisoConDuplicado } from "../componentes/AvisoDuplicado.tsx";
import { AvisoError, FormPaciente, hoy, pacienteVacio, problemasPaciente, Seccion, useAccion, type DatosPaciente } from "./comun.tsx";
import { ErrorApi } from "../api.ts";
import { PlanPagos } from "./Plan.tsx";
import { FotoPerfil } from "../componentes/FotoPerfil.tsx";
import { Documentos } from "./Documentos.tsx";
import { ClubPaciente, ClubFicha } from "./Club.tsx";
import { SelectorMateriales, materialesParaApi, olvidarInventario, type MaterialElegido } from "../componentes/SelectorMateriales.tsx";
import { PendientesDelPaciente, RegistrarEnNombre } from "./Firmas.tsx";
import { Fotos } from "./Fotos.tsx";
import { OrdenesPaciente } from "./Laboratorio.tsx";
import { Especialidades } from "./Especialidades.tsx";
import { CitasPaciente } from "./CitasPaciente.tsx";
import { SugerenciaMotivo } from "../componentes/SugerenciaMotivo.tsx";
import { AlertasPaciente } from "../componentes/AlertasPaciente.tsx";
import { PestanaUniversidad } from "./Universidad.tsx";
import { BuscadorCie10 } from "../componentes/BuscadorCie10.tsx";
import { LeyendaOdontograma } from "../componentes/LeyendaOdontograma.tsx";
import { HistoriaClinica } from "./Historia.tsx";
import { LineaTiempo } from "./LineaTiempo.tsx";
import { AutorizacionDatosAlAbrir } from "../componentes/AutorizacionDatosAlAbrir.tsx";

const NOMBRES = NOMBRE_TIPO as Record<string, string>;

export function PacienteVista({ id, pestana, irA }: { id: string; pestana: string; irA: (t: string) => void }) {
  const [p, setP] = useState<Paciente | null>(null);
  const [odo, setOdo] = useState<Odontograma | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [versionClub, setVersionClub] = useState(0);
  const clinico = puede("clinico:leer");

  useEffect(() => { api<Paciente>("GET", `/api/pacientes/${id}`).then(setP).catch(setError); }, [id]);
  // el plan puede cambiar el odontograma (al marcar realizado): se recarga al volver a esa pestaña
  useEffect(() => { if (clinico && pestana === "odontograma") api<Odontograma>("GET", `/api/pacientes/${id}/odontograma`).then(setOdo).catch(setError); }, [id, pestana]);

  if (error && !p) return <AvisoError error={error} />;
  if (!p) return <p className="muted">Cargando…</p>;
  // la administración puede dejar a un paciente de prueba fuera de reportes, comisiones y mensajes automáticos
  async function marcarPrueba() {
    const nuevo = !p!.es_prueba;
    if (!confirm(nuevo ? "¿Marcar a este paciente como de prueba? No contará en los reportes ni en las comisiones y no recibirá recordatorios ni mensajes automáticos. Sus datos no se borran." : "¿Quitar la marca de prueba? Volverá a contar en los reportes y a recibir mensajes automáticos.")) return;
    try { setP(await api<Paciente>("PUT", `/api/pacientes/${p!.id}`, { ...p, esPrueba: nuevo })); } catch (e) { setError(e); }
  }
  async function volverAActivar() {
    if (!confirm("¿Volver a activar esta ficha? Volverá a salir en las búsquedas, la agenda y los mensajes. Si sigue duplicada, aparecerá otra vez en Configuración → Pacientes duplicados.")) return;
    try { await api("POST", `/api/pacientes/${p!.id}/desarchivar`); setP(await api<Paciente>("GET", `/api/pacientes/${p!.id}`)); } catch (er) { setError(er); }
  }
  const e = p.nacimiento ? edad(p.nacimiento, hoy()) : null;
  // orden de las pestañas; cada rol ve solo las que le corresponden
  const pestanas = ([
    ["consulta", "Consulta", clinico], ["ficha", "Ficha", true], ["odontograma", "Odontograma", clinico], ["evolucion", "Evolución", clinico], ["tiempo", "Línea de tiempo", clinico || puede("caja:leer")],
    ["especialidades", "Especialidades", clinico && incluye("especialidades")], ["universidad", "Clínica universitaria", clinico && !!leerSesion()?.usuario.academico], ["plan", "Plan y pagos", clinico || puede("caja:leer")], ["fotos", "Fotos", clinico && incluye("fotos")],
    ["documentos", "Recetas y consentimientos", clinico],
    ["laboratorio", "Laboratorio", puede("laboratorio:leer") && incluye("laboratorio")],
  ] as [string, string, boolean][]).filter(([, , ver]) => ver).map(([k, t]) => [k, t] as [string, string]);
  const actual = pestanas.some(([k]) => k === pestana) ? pestana : pestanas[0][0];

  return (
    <>
      {!p.archivada && <AutorizacionDatosAlAbrir paciente={p} />}
      <div className="cabecera">
        <FotoPerfil pacienteId={p.id} nombre={`${p.nombres} ${p.apellidos}`} foto={p.foto ?? null} alCambiar={(foto) => setP({ ...p, foto })} />
        <h1>{p.nombres} {p.apellidos}</h1>
        <div className="meta">
          <span className="mono">HC {p.hc}</span>
          {e !== null && <span>{e} años</span>}
          {p.doc && <span className="mono">{p.doc}</span>}
          {p.telefono && <span className="mono">{p.telefono}</span>}
          {p.representante?.nombre && <span className="chip">Representante: {p.representante.nombre}</span>}
          {!p.representante?.nombre && p.filiacion?.representante_pendiente === "1" && <span className="chip rojo" title="Es menor de edad y se importó sin representante: agrégalo en la filiación">Falta el representante legal</span>}
          {p.es_prueba && <span className="chip rojo" title="No cuenta en reportes ni comisiones y no recibe mensajes automáticos">Paciente de prueba</span>}
          {puede("config:admin") && <button className="enlace" onClick={marcarPrueba}>{p.es_prueba ? "Quitar la marca de prueba" : "Marcar como paciente de prueba"}</button>}
          <ClubPaciente pacienteId={p.id} version={versionClub} />
        </div>
        {p.archivada && (
          <div className="aviso" role="alert" style={{ margin: "8px 0", display: "grid", gap: 6 }}>
            <b>Esta ficha está archivada porque estaba duplicada.</b>
            <span>Solo se puede consultar: ya no se le agregan citas, notas, cobros ni cambios.{p.archivada.motivo ? ` Motivo: ${p.archivada.motivo}.` : ""}</span>
            <span className="fila" style={{ flexWrap: "wrap", gap: 8 }}>
              {p.archivada.principal && <a className="btn chico pri" href={`#/paciente/${p.archivada.principal.id}/ficha`}>Ir a la ficha principal: {p.archivada.principal.nombres} {p.archivada.principal.apellidos} · HC {p.archivada.principal.hc}</a>}
              {puede("config:admin") && <button className="btn chico" onClick={volverAActivar}>Volver a activar esta ficha</button>}
            </span>
          </div>
        )}
        {p.vinculadas && p.vinculadas.length > 0 && (
          <div className="aviso info" role="status" style={{ margin: "8px 0", display: "grid", gap: 4 }}>
            <b>Fichas anteriores de este paciente</b>
            <span>Estaban duplicadas y se archivaron. Sus historias, recetas, notas y recibos siguen guardados y se consultan abriéndolas:</span>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {p.vinculadas.map((v) => (
                <li key={v.id}>
                  <a href={`#/paciente/${v.id}/ficha`}>{v.nombres} {v.apellidos} · HC {v.hc}</a> (archivada el {v.archivado_en.slice(0, 10).split("-").reverse().join("/")})
                  {v.saldo > 0 && <b style={{ marginLeft: 6 }}>· Debe {dinero(v.saldo)}</b>}
                  {v.porHacer > 0 && <span style={{ marginLeft: 6 }}>· {v.porHacer} tratamiento{v.porHacer === 1 ? "" : "s"} por hacer</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {p.registro_rapido && (
          <div className={`aviso ${p.ficha_rapida?.avisar ? "error" : "info"}`} role={p.ficha_rapida?.avisar ? "alert" : undefined} style={{ margin: "8px 0" }}>
            <b>Registro rápido.</b> Se agendó solo con nombres y celular. En la primera cita completa la cédula o el pasaporte, la fecha de nacimiento y la historia clínica.{" "}
            {p.ficha_rapida && (p.ficha_rapida.vencida
              ? <><b>Ya pasó el plazo ({p.ficha_rapida.dias} días): no se puede registrar nada clínico hasta completar la ficha.</b>{" "}</>
              : p.ficha_rapida.avisar ? <>Lleva {p.ficha_rapida.dias} días: te quedan <b>{p.ficha_rapida.restan}</b> para completarla antes de que se bloquee lo clínico.{" "}</> : null)}
            {puede("pacientes:escribir") && <button className="enlace" onClick={() => irA("ficha")}>Completar la ficha</button>}
          </div>
        )}
        {clinico && <AlertasPaciente pacienteId={p.id} alAbrirFicha={() => irA("ficha")} />}
        {clinico && <button className="btn chico cab-pdf" onClick={() => { location.hash = `#/expediente/${p.id}`; }}>Historia completa (PDF)</button>}
        {clinico && <button className="btn chico" onClick={() => { location.hash = `#/formulario-033/${p.id}`; }} title="Imprime la historia en el formato del formulario 033 del MSP">Formulario 033</button>}
      </div>
      <nav className="pestanas" role="tablist">
        {pestanas.map(([k, t]) => <button key={k} role="tab" aria-selected={k === actual} onClick={() => irA(k)}>{t}</button>)}
      </nav>
      {actual === "ficha" && <><CitasPaciente pacienteId={p.id} /><Ficha p={p} alGuardar={setP} versionClub={versionClub} alCambiarClub={() => setVersionClub((v) => v + 1)} /></>}
      {actual === "odontograma" && !odo && <p className="muted">Cargando…</p>}
      {actual === "odontograma" && odo && <PestanaOdontograma id={id} odo={odo} setOdo={setOdo} />}
      {actual === "consulta" && <Consulta id={id} irA={irA} alAplicar={(o) => { if (o) setOdo(o); }} />}
      {actual === "plan" && <PlanPagos paciente={p} />}
      {actual === "documentos" && <Documentos paciente={p} />}
      {actual === "fotos" && <Fotos paciente={p} />}
      {actual === "laboratorio" && <OrdenesPaciente paciente={p} />}
      {actual === "especialidades" && <Especialidades paciente={p} />}
      {actual === "universidad" && <PestanaUniversidad paciente={p} irA={irA} />}
      {actual === "evolucion" && <Evolucion id={id} />}
      {actual === "tiempo" && <LineaTiempo paciente={p} irA={irA} />}
    </>
  );
}

/* ---------------- Ficha: filiación + historia (formato de casillas, en Historia.tsx) ---------------- */
function Ficha({ p, alGuardar, versionClub, alCambiarClub }: { p: Paciente; alGuardar: (p: Paciente) => void; versionClub: number; alCambiarClub: () => void }) {
  const [datos, setDatos] = useState<DatosPaciente>(p);
  const [ok, setOk] = useState("");
  const [noEsDuplicado, setNoEsDuplicado] = useState(false);
  const f = useAccion();
  const escribe = puede("pacientes:escribir");
  // botón final: guarda la filiación y la historia clínica sin subir al principio de la página
  const [historiaSucia, setHistoriaSucia] = useState(false);
  const guardarHistoria = useRef<(() => Promise<unknown>) | null>(null);
  const [final, setFinal] = useState<{ ok: string; error: unknown }>({ ok: "", error: null });
  const [guardandoTodo, setGuardandoTodo] = useState(false);
  const datosP = p as unknown as Record<string, unknown>, editados = datos as unknown as Record<string, unknown>;
  const filiacionSucia = Object.keys(pacienteVacio()).some((k) => JSON.stringify(editados[k]) !== JSON.stringify(datosP[k]));
  const hayCambios = filiacionSucia || historiaSucia;

  /** Devuelve true si se guardó (o no había nada que guardar). */
  async function guardarFicha(): Promise<boolean> {
    const probs = problemasPaciente(datos);
    if (probs.length) { f.setError(new ErrorApi(400, "Revisa los datos.", probs)); return false; }
    const r = await f.correr(() => api<Paciente>("PUT", `/api/pacientes/${p.id}`, { ...datos, confirmarDuplicado: noEsDuplicado }));
    if (r) { alGuardar(r); setOk("Filiación guardada."); }
    return !!r;
  }
  async function guardarTodo() {
    setGuardandoTodo(true); setFinal({ ok: "", error: null });
    const hechos: string[] = [];
    try {
      if (filiacionSucia) {
        const probs = problemasPaciente(datos);
        if (probs.length) return setFinal({ ok: "", error: new ErrorApi(400, "Revisa los datos de la filiación.", probs) });
        try { const r = await api<Paciente>("PUT", `/api/pacientes/${p.id}`, { ...datos, confirmarDuplicado: noEsDuplicado }); alGuardar(r); setOk("Filiación guardada."); hechos.push("la filiación"); }
        catch (e) { return setFinal({ ok: "", error: e }); }
      }
      if (historiaSucia && guardarHistoria.current) {
        const fallo = await guardarHistoria.current();
        if (fallo) return setFinal({ ok: hechos.length ? `Se guardó ${hechos.join(" y ")}, pero no la historia clínica.` : "", error: fallo });
        hechos.push("la historia clínica");
      }
      setFinal({ ok: hechos.length ? `Guardado: ${hechos.join(" y ")}.` : "No había cambios por guardar.", error: null });
    } finally { setGuardandoTodo(false); }
  }
  return (
    <>
      {ok && <div className="aviso ok" role="status">{ok}</div>}
      <Seccion titulo="Filiación" acciones={escribe && <button className="btn pri" onClick={guardarFicha} disabled={f.ocupado}>Guardar</button>}>
        <FormPaciente valor={datos} cambiar={(v) => { setDatos(v); setOk(""); setFinal({ ok: "", error: null }); }} soloLectura={!escribe} />
        <AvisoConDuplicado error={f.error} confirmado={noEsDuplicado} alCambiar={setNoEsDuplicado} />
      </Seccion>
      {puede("pacientes:leer") && <ClubFicha pacienteId={p.id} nombre={`${p.nombres} ${p.apellidos}`} version={versionClub} alCambiar={alCambiarClub} />}
      {puede("clinico:leer") && <HistoriaClinica pacienteId={p.id} paciente={p} sexo={datos.sexo} guardarRef={guardarHistoria} alCambiarSucio={(v) => { setHistoriaSucia(v); if (v) setFinal({ ok: "", error: null }); }} />}
      {(escribe || puede("clinico:escribir")) && (
        <div className="barra-guardar" role="region" aria-label="Guardar la ficha">
          <div className="barra-guardar-estado" aria-live="polite">
            {final.error ? <AvisoConDuplicado error={final.error} confirmado={noEsDuplicado} alCambiar={setNoEsDuplicado} />
              : hayCambios ? <span className="chip rojo">Cambios sin guardar{filiacionSucia && historiaSucia ? " en la filiación y en la historia" : filiacionSucia ? " en la filiación" : " en la historia"}</span>
              : final.ok ? <span className="ok">{final.ok}</span>
              : <span className="muted">Todo guardado.</span>}
          </div>
          <button className="btn pri" onClick={guardarTodo} disabled={guardandoTodo || !hayCambios}>{guardandoTodo ? "Guardando…" : "Guardar cambios"}</button>
        </div>
      )}
    </>
  );
}

/* ---------------- Odontograma ---------------- */
function PestanaOdontograma({ id, odo, setOdo }: { id: string; odo: Odontograma; setOdo: (o: Odontograma) => void }) {
  const [sel, setSel] = useState<number | null>(null);
  const [tipo, setTipo] = useState<Hallazgo["tipo"]>("caries");
  const [estado, setEstado] = useState<Hallazgo["estado"]>("por_realizar");
  const [caras, setCaras] = useState<string[]>([]);
  const [cie, setCie] = useState("");
  const [nota, setNota] = useState("");
  const [grado, setGrado] = useState(1);
  const [arcada, setArcada] = useState("Superior");
  const [pilarA, setPilarA] = useState(""), [pilarB, setPilarB] = useState("");
  const piezasPuente = esTramo(tipo) ? rangoPuente(Number(pilarA || sel || 0), Number(pilarB || 0)) : null;
  const a = useAccion();
  const escribe = puede("clinico:escribir");
  const deLaPieza = odo.hallazgos.filter((h) => h.diente === sel);
  const generales = odo.hallazgos.filter((h) => h.diente === null);

  const alternar = (c: string) => setCaras((xs) => (xs.includes(c) ? xs.filter((x) => x !== c) : [...xs, c]));
  async function agregar() {
    if (esTramo(tipo)) {
      // de pilar a pilar: se registran todas las piezas de la prótesis fija o removible
      if (!piezasPuente || piezasPuente.length < 3) return a.setError(new Error(`La ${NOMBRES[tipo].toLowerCase()} va de pilar a pilar: elige dos pilares de la misma arcada con al menos una pieza entre ellos.`));
      const r = await a.correr(() => api<Odontograma>("POST", `/api/pacientes/${id}/hallazgos`, { hallazgos: piezasPuente.map((n) => ({ diente: n, tipo, estado, superficies: [], cie10: null, nota: notaTramo(tipo, piezasPuente) })) }));
      if (r) { setOdo(r); setPilarA(""); setPilarB(""); }
      return;
    }
    const r = await a.correr(() => api<Odontograma>("POST", `/api/pacientes/${id}/hallazgos`, { hallazgos: [{ diente: tipo === "protesis_total" ? null : sel, tipo, estado, superficies: caras, cie10: cie || null, nota: tipo === "protesis_total" ? arcada : nota, grado: TIPOS_CON_GRADO.includes(tipo) ? grado : null }] }));
    if (r) { setOdo(r); setCaras([]); setNota(""); }
  }
  /** Recuadros de recesión y movilidad (033): vacío → 1 → 2 → 3 → 4 → vacío. Usa los mismos hallazgos que el resto del odontograma. */
  async function gradoPeriodonto(n: number, tipo: "recesion" | "movilidad", grado: number | null) {
    if (a.ocupado) return;
    const previo = odo.hallazgos.find((h) => h.diente === n && h.tipo === tipo);
    const r = grado === null
      ? (previo ? await a.correr(() => api<Odontograma>("DELETE", `/api/pacientes/${id}/hallazgos/${previo.id}`)) : undefined)
      : await a.correr(() => api<Odontograma>("POST", `/api/pacientes/${id}/hallazgos`, { hallazgos: [{ diente: n, tipo, estado: "por_realizar", superficies: [], cie10: null, nota: "", grado }] }));
    if (r) setOdo(r);
  }
  async function quitar(h: HallazgoApi) {
    if (!confirm(`¿Quitar "${describirHallazgo(h, NOMBRES)}"${h.diente ? ` de la pieza ${h.diente}` : ""}? Queda registrado en la auditoría.`)) return;
    const r = await a.correr(() => api<Odontograma>("DELETE", `/api/pacientes/${id}/hallazgos/${h.id}`));
    if (r) setOdo(r);
  }
  const itemHallazgo = (h: HallazgoApi) => (
    <li key={h.id} style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid var(--line)", padding: "6px 0" }}>
      <span><span className={`chip ${h.estado === "realizado" ? "azul" : "rojo"}`}>{h.estado === "realizado" ? "Realizado" : "Por realizar"}</span> {describirHallazgo(h, NOMBRES)}{h.nota && h.tipo !== "protesis_total" && <span className="muted"> · {h.nota}</span>}</span>
      {escribe && <button className="btn chico peligro" onClick={() => quitar(h)} aria-label="Quitar hallazgo">Quitar</button>}
    </li>
  );

  return (
    <div className="dos">
      <Seccion titulo="Odontograma">
        <OdontogramaSVG hallazgos={odo.hallazgos} denticion={odo.denticion} seleccion={sel} carasMarcadas={caras}
          alElegir={(n) => { if (n !== sel) setCaras([]); setSel(n); }} alTocarCara={escribe ? (_n, c) => alternar(c) : undefined} alGradoPeriodonto={escribe ? gradoPeriodonto : undefined} />
        <div className="indices">
          <span>Dentición: <b>{odo.denticion}</b></span>
          {indicesPorDenticion(odo.denticion).cpo && <span>CPO-D: <b>{odo.cpod.total}</b> <span className="muted">(C {odo.cpod.cariados} · P {odo.cpod.perdidos} · O {odo.cpod.obturados})</span></span>}
          {indicesPorDenticion(odo.denticion).ceo && <span>ceo-d: <b>{odo.ceod.total}</b> <span className="muted">(c {odo.ceod.cariados} · e {odo.ceod.perdidos} · o {odo.ceod.obturados})</span></span>}
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>Toca una pieza para verla; toca sus caras para marcarlas. En los recuadros, toca para cambiar: recesión de Miller 1, 2, 3, 4 y vacío; movilidad 1, 2, 3 y vacío.</p>
        <LeyendaOdontograma />
      </Seccion>
      <Seccion titulo={sel ? `Pieza ${sel}` : "Hallazgos generales"}>
        {sel && <p className="muted" style={{ margin: 0 }}>{nombrePieza(sel)}</p>}
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>{(sel ? deLaPieza : generales).map(itemHallazgo)}</ul>
        {(sel ? deLaPieza : generales).length === 0 && <p className="muted" style={{ margin: 0 }}>Sin hallazgos.</p>}
        {escribe && (
          <div className="card" style={{ boxShadow: "none", background: "var(--surface-2)" }}>
            <h3>Marcar manualmente{sel ? "" : " (sin pieza)"}</h3>
            <div className="rejilla" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))" }}>
              <label className="campo">Hallazgo<select value={tipo} onChange={(e) => setTipo(e.target.value as Hallazgo["tipo"])}>{TIPOS_HALLAZGO.map((t) => <option key={t} value={t}>{NOMBRES[t]}</option>)}</select></label>
              <label className="campo">Estado<select value={estado} onChange={(e) => setEstado(e.target.value as Hallazgo["estado"])}><option value="por_realizar">Por realizar</option><option value="realizado">Realizado</option></select></label>
            </div>
            {TIPOS_CON_GRADO.includes(tipo) && (
              <label className="campo">{tipo === "recesion" ? "Clase de Miller" : "Grado"}<select value={Math.min(grado, MAX_GRADO[tipo] ?? 4)} onChange={(e) => setGrado(Number(e.target.value))}>{Array.from({ length: MAX_GRADO[tipo] ?? 4 }, (_, i) => i + 1).map((g) => <option key={g} value={g}>{g}</option>)}</select></label>
            )}
            {esTramo(tipo) && (
              <div className="rejilla" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))" }}>
                <label className="campo">Pilar inicial<input className="mono" inputMode="numeric" maxLength={2} placeholder={sel ? String(sel) : "24"} value={pilarA} onChange={(e) => setPilarA(e.target.value.replace(/\D/g, ""))} /></label>
                <label className="campo">Pilar final<input className="mono" inputMode="numeric" maxLength={2} placeholder="26" value={pilarB} onChange={(e) => setPilarB(e.target.value.replace(/\D/g, ""))} /></label>
                <small className="hint" style={{ gridColumn: "1 / -1" }}>{piezasPuente && piezasPuente.length >= 3 ? `${tipo === "puente" ? "Puente" : "Prótesis removible"} de ${piezasPuente.length} piezas: ${piezasPuente.join(", ")} (pilares ${piezasPuente[0]} y ${piezasPuente[piezasPuente.length - 1]}).` : "Mínimo 3 piezas: dos pilares y el póntico entre ellos, en la misma arcada."}</small>
              </div>
            )}
            {tipo === "protesis_total" && (
              <label className="campo">Arcada<select value={arcada} onChange={(e) => setArcada(e.target.value)}><option>Superior</option><option>Inferior</option></select>
                <small className="hint">La prótesis total se registra para toda la arcada.</small></label>
            )}
            {sel && <div className="sup" role="group" aria-label="Superficies">{SUPERFICIES.map((s) => <button key={s} type="button" aria-pressed={caras.includes(s)} onClick={() => alternar(s)}>{s}</button>)}</div>}
            <BuscadorCie10 valor={cie} alCambiar={setCie} sugeridos={CIE_POR_HALLAZGO[tipo] ?? []} obligatorio={tipo === "diagnostico"} />
            <label className="campo">Nota<input maxLength={200} value={nota} onChange={(e) => setNota(e.target.value)} /></label>
            <button className="btn pri" onClick={agregar} disabled={a.ocupado}>Agregar</button>
          </div>
        )}
        <AvisoError error={a.error} />
      </Seccion>
    </div>
  );
}

/* ---------------- Consulta: dictado → revisar → aplicar ---------------- */
type CampoTexto = { campo: string; grupo: "anamnesis" | "examen"; texto: string; usar: boolean };
interface Propuesta { motor: string; campos: CampoTexto[]; hallazgos: (Hallazgo & { usar: boolean })[] }

function Consulta({ id, alAplicar, irA }: { id: string; alAplicar: (o: Odontograma | null) => void; irA: (t: string) => void }) {
  const [texto, setTexto] = useState("");
  const [propuesta, setPropuesta] = useState<Propuesta | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);
  const [vuelta, setVuelta] = useState(0);
  const a = useAccion();
  if (!puede("clinico:escribir")) return <p className="muted">Tu rol puede ver la historia, pero no registrar la consulta.</p>;

  async function interpretar() {
    setHecho(null);
    const r = await a.correr(() => api<{ motor: string; hallazgos: Hallazgo[]; anamnesis: Record<string, string>; examen: Record<string, string> }>("POST", "/api/dictado/consulta", { texto }));
    if (r) setPropuesta({
      motor: r.motor,
      campos: [
        ...CAMPOS_ANAMNESIS.filter((k) => r.anamnesis?.[k]).map((k) => ({ campo: k, grupo: "anamnesis" as const, texto: r.anamnesis[k], usar: true })),
        ...CAMPOS_EXAMEN.filter((k) => r.examen?.[k]).map((k) => ({ campo: k, grupo: "examen" as const, texto: r.examen[k], usar: true })),
      ],
      hallazgos: r.hallazgos.map((h) => ({ ...h, usar: true })),
    });
    setVuelta((n) => n + 1);
  }
  function usarSugerencia(t: string, modo: "reemplazar" | "agregar") {
    const p = propuesta!;
    const i = p.campos.findIndex((c) => c.campo === "enfermedad_actual");
    if (i >= 0) return cambiarCampo(i, { texto: modo === "agregar" && p.campos[i].texto.trim() ? `${p.campos[i].texto.trim().replace(/([^.!?])$/, "$1.")} ${t}` : t, usar: true });
    const j = p.campos.findIndex((c) => c.campo === "motivo");
    const nuevo: CampoTexto = { campo: "enfermedad_actual", grupo: "anamnesis", texto: t, usar: true };
    setPropuesta({ ...p, campos: [...p.campos.slice(0, j + 1), nuevo, ...p.campos.slice(j + 1)] });
  }
  async function aplicar() {
    const p = propuesta!;
    const campos = p.campos.filter((c) => c.usar && c.texto.trim());
    const hallazgos = p.hallazgos.filter((h) => h.usar).map(({ usar: _u, ...h }) => h);
    const r = await a.correr(async () => {
      if (campos.length) await api("POST", `/api/pacientes/${id}/historia/dictado`, {
        anamnesis: Object.fromEntries(campos.filter((c) => c.grupo === "anamnesis").map((c) => [c.campo, c.texto])),
        examen: Object.fromEntries(campos.filter((c) => c.grupo === "examen").map((c) => [c.campo, c.texto])),
      });
      const o = hallazgos.length ? await api<Odontograma>("POST", `/api/pacientes/${id}/hallazgos`, { hallazgos }) : null;
      return { o };
    });
    if (r) {
      alAplicar(r.o);
      if (campos.length) dispatchEvent(new Event("donna:historia"));
      setHecho(`Guardado en la historia: ${campos.length ? campos.map((c) => NOMBRE_CAMPO[c.campo as keyof typeof NOMBRE_CAMPO].toLowerCase()).join(", ") : "nada"}${hallazgos.length ? ` · ${hallazgos.length} hallazgo(s) en el odontograma` : ""}.`);
      setPropuesta(null); setTexto("");
    }
  }
  const cambiarCampo = (i: number, x: Partial<CampoTexto>) => setPropuesta({ ...propuesta!, campos: propuesta!.campos.map((c, j) => (j === i ? { ...c, ...x } : c)) });
  const algo = propuesta && (propuesta.campos.some((c) => c.usar) || propuesta.hallazgos.some((h) => h.usar));

  return (
    <div className="dos">
      <Seccion titulo="Dictado de la consulta">
        <Dictador texto={texto} setTexto={setTexto} placeholder="Ej.: Acude porque le duele una muela de abajo desde hace tres días. Es hipertensa, toma losartán 50 mg. Alérgica a la penicilina. Su madre es diabética. Caries mesio oclusal en la 36, gingivitis generalizada." />
        <button className="btn pri" onClick={interpretar} disabled={a.ocupado || !texto.trim()}>{a.ocupado && !propuesta ? "Interpretando…" : "Interpretar"}</button>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>Puedes dictar todo seguido: motivo de consulta, enfermedad actual, antecedentes personales y familiares, alergias, medicación, hábitos, signos vitales, examen y hallazgos por pieza. También sirve decir los encabezados («antecedentes familiares…»). Solo se envía el texto dictado; nunca el nombre ni la cédula del paciente. Nada se guarda hasta que lo apliques.</p>
      </Seccion>
      <Seccion titulo="Revisar antes de guardar" acciones={propuesta && <span className="chip">{propuesta.motor === "ia" ? "Interpretado con IA" : "Reglas locales"}</span>}>
        {hecho && <div className="aviso ok" role="status">{hecho} <button className="enlace" onClick={() => irA("ficha")}>Ver ficha</button> · <button className="enlace" onClick={() => irA("odontograma")}>Ver odontograma</button></div>}
        {!propuesta && !hecho && <p className="muted" style={{ margin: 0 }}>Aquí aparecerá lo que Donna entendió, separado por secciones de la historia clínica y del odontograma.</p>}
        {propuesta && !propuesta.campos.length && !propuesta.hallazgos.length && <div className="aviso info">No se reconoció nada. Revisa el texto o dicta con encabezados («motivo de consulta…», «alergias…»).</div>}
        {propuesta && propuesta.campos.length > 0 && (
          <>
            <h3>Historia clínica</h3>
            <ul className="revision">
              {propuesta.campos.map((c, i) => (
                <li key={c.campo} className={`campo-dictado${c.usar ? "" : " off"}`}>
                  <input type="checkbox" checked={c.usar} aria-label={`Guardar ${NOMBRE_CAMPO[c.campo as keyof typeof NOMBRE_CAMPO]}`} onChange={() => cambiarCampo(i, { usar: !c.usar })} />
                  <label className="campo" style={{ flex: 1 }}>{NOMBRE_CAMPO[c.campo as keyof typeof NOMBRE_CAMPO]}
                    <textarea rows={2} style={{ minHeight: 44 }} value={c.texto} onChange={(e) => cambiarCampo(i, { texto: e.target.value })} />
                    {c.usar && marcasDeCampo(c.campo, c.texto).length > 0 && (
                      <span className="marcas" aria-label={`Se marcará en ${NOMBRE_CAMPO[c.campo as keyof typeof NOMBRE_CAMPO]}`}>
                        <span className="muted">Se marcará:</span> {marcasDeCampo(c.campo, c.texto).map((m) => <span key={m} className="tog mini" aria-pressed="true">{m}</span>)}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
        {propuesta && propuesta.campos.some((c) => c.campo === "motivo" || c.campo === "enfermedad_actual") && (
          <SugerenciaMotivo key={vuelta} automatico pacienteId={id}
            motivo={propuesta.campos.find((c) => c.campo === "motivo")?.texto ?? ""}
            enfermedad={propuesta.campos.find((c) => c.campo === "enfermedad_actual")?.texto ?? ""}
            alUsar={usarSugerencia} />
        )}
        {propuesta && propuesta.hallazgos.length > 0 && (
          <>
            <h3>Odontograma</h3>
            <ul className="revision">
              {propuesta.hallazgos.map((h, i) => (
                <li key={i} className={h.usar ? "" : "off"}>
                  <input type="checkbox" checked={h.usar} aria-label="Aplicar este hallazgo"
                    onChange={() => setPropuesta({ ...propuesta, hallazgos: propuesta.hallazgos.map((x, j) => (j === i ? { ...x, usar: !x.usar } : x)) })} />
                  <b className="mono">{h.diente ?? "Gral."}</b>
                  <span>{describirHallazgo(h, NOMBRES)}</span>
                  {h.tipo === "caries"
                    ? <span className="chip rojo">Por realizar</span>
                    : <button type="button" className={`chip ${h.estado === "realizado" ? "azul" : "rojo"}`} title="Cambiar entre por realizar (rojo) y realizado (azul)"
                        onClick={() => setPropuesta({ ...propuesta, hallazgos: propuesta.hallazgos.map((x, j) => (j === i ? { ...x, estado: x.estado === "realizado" ? "por_realizar" : "realizado" } : x)) })}>
                        {h.estado === "realizado" ? "Realizado" : "Por realizar"}
                      </button>}
                </li>
              ))}
            </ul>
          </>
        )}
        {propuesta && (propuesta.campos.length > 0 || propuesta.hallazgos.length > 0) && (
          <>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Lo dictado se agrega a lo que ya estaba escrito en la ficha; no borra nada.</p>
            <div className="fila">
              <button className="btn pri" onClick={aplicar} disabled={a.ocupado || !algo}>Guardar en la historia{propuesta.hallazgos.some((h) => h.usar) ? " y el odontograma" : ""}</button>
              <button className="btn" onClick={() => setPropuesta(null)}>Descartar</button>
            </div>
          </>
        )}
        <AvisoError error={a.error} />
      </Seccion>
    </div>
  );
}

/* ---------------- Evolución: redactar → firmar; adendas ---------------- */
function Evolucion({ id }: { id: string }) {
  const [notas, setNotas] = useState<Nota[]>([]);
  const [texto, setTexto] = useState("");
  const [borrador, setBorrador] = useState<{ nota: string; piezas: number[]; motor: string } | null>(null);
  const [tipo, setTipo] = useState<"nota" | "epicrisis">("nota");
  const [revisado, setRevisado] = useState(false);
  const [adenda, setAdenda] = useState<Record<string, string>>({});
  const [materiales, setMateriales] = useState<MaterialElegido[]>([]);
  const [delegando, setDelegando] = useState(false);
  const [versionDelegados, setVersionDelegados] = useState(0);
  const a = useAccion();
  const firma = puede("clinico:firmar");

  useEffect(() => { api<Nota[]>("GET", `/api/pacientes/${id}/notas`).then(setNotas).catch(a.setError); }, [id]);

  async function redactar() {
    const r = await a.correr(() => api<{ motor: string; borrador: { nota: string; piezas: number[] } }>("POST", "/api/dictado/nota", { texto }));
    if (r) { setBorrador({ ...r.borrador, motor: r.motor }); setRevisado(false); }
  }
  async function firmar() {
    const n = await a.correr(() => api<Nota>("POST", `/api/pacientes/${id}/notas`, { texto: borrador!.nota, piezas: borrador!.piezas, tipo, materiales: materialesParaApi(materiales) }));
    if (n) { setNotas([n, ...notas]); setBorrador(null); setTexto(""); setRevisado(false); setMateriales([]); olvidarInventario(); }
  }
  async function agregarAdenda(notaId: string) {
    const n = await a.correr(() => api<Nota>("POST", `/api/notas/${notaId}/adendas`, { texto: adenda[notaId] }));
    if (n) { setNotas(notas.map((x) => (x.id === n.id ? n : x))); setAdenda({ ...adenda, [notaId]: "" }); }
  }

  return (
    <div className="dos">
      <div style={{ display: "grid", gap: 16 }}>
        {firma && (
          <Seccion titulo="Nueva nota">
            <Dictador texto={texto} setTexto={setTexto} placeholder="Ej.: se realiza resina oclusodistal en la 46 con anestesia infiltrativa, sin complicaciones; control en 15 días" />
            <button className="btn" onClick={redactar} disabled={a.ocupado || !texto.trim()}>Redactar con Donna</button>
            {borrador && (
              <div className="card" style={{ boxShadow: "none", background: "var(--surface-2)" }}>
                <div className="fila" style={{ justifyContent: "space-between" }}>
                  <h3>Borrador</h3><span className="chip">{borrador.motor === "ia" ? "Redactado con IA" : "Reglas locales"}</span>
                </div>
                <label className="campo">Tipo
                  <select value={tipo} onChange={(e) => setTipo(e.target.value as "nota" | "epicrisis")}><option value="nota">Nota de evolución</option><option value="epicrisis">Epicrisis</option></select>
                </label>
                <textarea className="entrada" style={{ minHeight: 140 }} value={borrador.nota} onChange={(e) => { setBorrador({ ...borrador, nota: e.target.value }); setRevisado(false); }} aria-label="Texto de la nota" />
                {borrador.piezas.length > 0 && <p style={{ margin: 0 }} className="muted">Piezas: <span className="mono">{borrador.piezas.join(", ")}</span></p>}
                <label className="firmar"><input type="checkbox" checked={revisado} onChange={(e) => setRevisado(e.target.checked)} /> Revisé la nota. Al firmar no se podrá modificar, solo agregar adendas.</label>
                {puede("inventario:usar") && incluye("inventario") && (
                  <div>
                    <b style={{ fontSize: 14 }}>Materiales usados en este tratamiento</b>
                    <p className="muted" style={{ margin: "2px 0 6px", fontSize: 13 }}>Cartuchos de anestesia, agujas, suturas, gasas… Se descuentan del inventario y quedan escritos al final de la nota.</p>
                    <SelectorMateriales valor={materiales} alCambiar={setMateriales} />
                  </div>
                )}
                <button className="btn pri" onClick={firmar} disabled={a.ocupado || !revisado || !borrador.nota.trim()}>Firmar nota</button>
              </div>
            )}
          </Seccion>
        )}
        <AvisoError error={a.error} />
      </div>
      <Seccion titulo="Evolución">
        <PendientesDelPaciente pacienteId={id} version={versionDelegados} />
        {puede("clinico:delegar") && incluye("delegacion") && (delegando
          ? <RegistrarEnNombre pacienteId={id} alCancelar={() => setDelegando(false)} alTerminar={() => { setDelegando(false); setVersionDelegados((v) => v + 1); }} />
          : <p><button className="btn chico" onClick={() => setDelegando(true)}>Registrar evolución en nombre de un profesional</button></p>)}
        {notas.length === 0 && <p className="muted" style={{ margin: 0 }}>Aún no hay notas firmadas.</p>}
        {notas.map((n) => (
          <article key={n.id} className="nota">
            <div className="muted" style={{ fontSize: 13 }}>
              {n.tipo === "epicrisis" && <span className="chip">Epicrisis</span>} {new Date(n.firmada_en).toLocaleString("es-EC", { dateStyle: "medium", timeStyle: "short" })} · Firmó {n.firmada_por}
              {n.piezas.length > 0 && <> · Piezas <span className="mono">{n.piezas.join(", ")}</span></>}
            </div>
            <p>{n.texto}</p>
            {n.adendas.map((d) => (
              <div key={d.id} className="adenda"><span className="muted">Adenda · {new Date(d.creado).toLocaleString("es-EC", { dateStyle: "short", timeStyle: "short" })} · {d.autor}:</span> {d.texto}</div>
            ))}
            {firma && (
              <div className="fila">
                <input className="entrada" style={{ flex: 1, minWidth: 180 }} placeholder="Agregar adenda" value={adenda[n.id] ?? ""} onChange={(e) => setAdenda({ ...adenda, [n.id]: e.target.value })} />
                <button className="btn chico" onClick={() => agregarAdenda(n.id)} disabled={a.ocupado || !(adenda[n.id] ?? "").trim()}>Agregar</button>
              </div>
            )}
          </article>
        ))}
      </Seccion>
    </div>
  );
}
